import { Injectable, OnDestroy, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import { EventSourcePolyfill } from 'event-source-polyfill/src/eventsource'

import { distinctUntilChanged } from 'rxjs/operators'

import { AuthConfigService } from '@flowx/designer/modules/auth/auth.service'
import { SSE_HEARTBEAT_TIMEOUT_MS } from '@flowx/designer/shared/api/sse.constants'

import { NotificationsService } from './notifications.service'

import { NotificationsCenterStore } from '../notifications-center/notifications-center.store'
import {
  NotificationReadEvent,
  NotificationSseEventType,
  NotificationUnreadEvent,
  UserNotificationEvent,
  extendNotification,
} from '../notifications.model'

/**
 * Dedicated per-user notification channel — see the "Async User Notifications over SSE"
 * spec. Identity and organization are derived server-side from the token, so there is no
 * namespace, and `offset=LATEST` because phase 1 deliberately replays nothing emitted
 * while the user was away.
 *
 * A dropped connection therefore loses whatever arrived during the gap, and that is accepted
 * rather than patched with a reconnect-triggered refetch: the window is a second or two for any
 * failure the transport reports, and a long disconnection expires the token, whose `false → true`
 * transition reloads the history anyway. Closing it properly means a frame on reconnect carrying
 * the last emitted `notificationId` to compare against the newest the center holds.
 *
 * Reconnects are left to `EventSourcePolyfill`, which retries with its own backoff and
 * no ceiling — deliberately unlike the `SSE_MAX_RETRIES` cap the app's other SSE
 * services apply. Those are page-scoped and can be re-established by navigating away
 * and back; this one has to survive the whole session, and giving up would leave no
 * user-visible surface to retry from.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsSseService implements OnDestroy {
  #authConfigService = inject(AuthConfigService)
  #notificationsService = inject(NotificationsService)
  #notificationsCenterStore = inject(NotificationsCenterStore)
  #baseUrl = inject<string>('BASE_URL' as any)

  #connection: EventSource | undefined

  constructor() {
    // Follows token validity: connects once there's a valid access token and closes on
    // logout/expiry, so the stream never outlives the session. `distinctUntilChanged` is
    // load-bearing — `isAuthenticated$` re-emits on every OAuth event.
    this.#authConfigService.isAuthenticated$
      .pipe(distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((isAuthenticated) => {
        if (isAuthenticated) {
          this.#connect()
          void this.#notificationsCenterStore.load()
        } else {
          this.#close()
        }
      })
  }

  ngOnDestroy(): void {
    this.#close()
  }

  #connect(): void {
    if (this.#connection) {
      return
    }

    this.#connection = new EventSourcePolyfill(
      `${this.#baseUrl}/api/events/notifications?offset=LATEST`,
      {
        // The token has to be resolved per connection attempt, not once. This channel
        // delegates reconnects to the polyfill, which keeps the one connection and
        // re-reads this header's value on each retry — so a plain string would pin
        // whichever token was current when `#connect()` ran, and the first reconnect
        // after a silent refresh would send an expired one. The polyfill treats that
        // 401 as fatal, so the channel would stay dead for the rest of the session,
        // and the `distinctUntilChanged` above deliberately hides refreshes from us.
        // `enumerable: true` is required: a non-enumerable accessor is skipped and the
        // header never sent at all.
        headers: Object.defineProperty({}, 'Authorization', {
          enumerable: true,
          get: () => `Bearer ${localStorage.getItem('access_token')}`,
        }),
        heartbeatTimeout: SSE_HEARTBEAT_TIMEOUT_MS,
        withCredentials: true,
      }
    )

    this.#connection.addEventListener(
      NotificationSseEventType.Notification,
      (event: MessageEvent) => this.#handleFrame(event)
    )

    this.#connection.addEventListener(NotificationSseEventType.Read, (event: MessageEvent) =>
      this.#handleReadFrame(event)
    )

    this.#connection.addEventListener(NotificationSseEventType.Unread, (event: MessageEvent) =>
      this.#handleUnreadFrame(event)
    )
  }

  #close(): void {
    this.#connection?.close()
    this.#connection = undefined
  }

  #handleFrame(event: MessageEvent): void {
    try {
      const notification = extendNotification(JSON.parse(event.data) as UserNotificationEvent)
      this.#notificationsCenterStore.add(notification)
      this.#notificationsService.push(notification)
    } catch (error) {
      console.error('[Notifications SSE] Failed to handle notification frame', error)
    }
  }

  /**
   * Read state belongs to the user, not to the tab that did the clicking, so this converges every
   * surface they have open — other tabs, other browsers, other devices. No toast: a read is not
   * news, and the tab that performed it has already applied it against the 200.
   */
  #handleReadFrame(event: MessageEvent): void {
    try {
      const { id } = JSON.parse(event.data) as NotificationReadEvent
      this.#notificationsCenterStore.applyServerRead(id)
    } catch (error) {
      console.error('[Notifications SSE] Failed to handle notification read frame', error)
    }
  }

  /** The unread counterpart of `#handleReadFrame`, for the same reasons. */
  #handleUnreadFrame(event: MessageEvent): void {
    try {
      const { id } = JSON.parse(event.data) as NotificationUnreadEvent
      this.#notificationsCenterStore.applyServerUnread(id)
    } catch (error) {
      console.error('[Notifications SSE] Failed to handle notification unread frame', error)
    }
  }
}
